import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Building2, MapPin, Play, Wrench, Quote } from "lucide-react";
import { Reveal } from "../components/common";
import { publicApi, mediaUrl } from "../lib/api";
import { videoEmbedUrl } from "../lib/utils";
import { RichContent } from "../lib/richText";
import Seo, { breadcrumbSchema, caseStudySchema } from "../components/Seo";
import ShareRow from "../components/ShareRow";

const SECTIONS = [
  { key: "client", label: "The Client" },
  { key: "challenge", label: "The Challenge" },
  { key: "solution", label: "The Solution" },
  { key: "difference", label: "The Difference" },
  { key: "outcome", label: "The Outcome" },
  { key: "closing", label: "" },
];

export default function SuccessStory() {
  const { slug } = useParams();
  const [item, setItem] = useState(null);
  const [services, setServices] = useState([]);
  const [all, setAll] = useState([]);
  const [playing, setPlaying] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    setPlaying(false);
    window.scrollTo(0, 0);
    publicApi.caseStudy(slug).then(setItem).catch(() => setItem(null)).finally(() => setLoading(false));
    publicApi.services().then(setServices).catch(() => {});
    publicApi.caseStudies().then(setAll).catch(() => {});
  }, [slug]);

  if (loading) return <div className="max-w-3xl mx-auto px-6 py-32 text-center text-slate-400">Loading...</div>;
  if (!item) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-32 text-center">
        <Seo title="Story not found" noindex />
        <h1 className="font-display font-extrabold text-3xl text-navy mb-4">Story not found</h1>
        <Link to="/testimonials" className="text-orange font-bold inline-flex items-center gap-2"><ArrowLeft size={16} /> Back to testimonials</Link>
      </div>
    );
  }

  const storyUrl = `${window.location.origin}/success-stories/${item.slug}`;
  const storyImage = item.image ? mediaUrl(item.image) : "";
  const uploaded = item.video_upload;
  const embed = videoEmbedUrl(item.video_url || "");
  const ytId = (item.video_url || "").match(/(?:youtu\.be\/|[?&]v=|embed\/|shorts\/|live\/)([A-Za-z0-9_-]{11})/)?.[1];
  const poster = ytId ? `https://img.youtube.com/vi/${ytId}/maxresdefault.jpg` : (uploaded ? mediaUrl(`${uploaded}/poster`) : "");
  const playUrl = embed.includes("youtube.com") ? `${embed}&autoplay=1` : `${embed}?autoplay=1`;
  const hasVideo = !!(uploaded || embed);

  const bodies = item.section_bodies || {};
  const headingFor = (s) => (item.section_headings?.[s.key] ?? "") || s.label;
  const activeSections = SECTIONS.filter((s) => (bodies[s.key] || "").trim());
  // Fallback for legacy stories with only the old `content` array
  const legacyParas = (!activeSections.length && (item.content || []).length) ? item.content : [];

  const results = (item.results || []).filter((r) => (r.heading || "").trim()).slice(0, 4);
  const testimonials = (item.testimonials || []).filter((t) => (t.quote || "").trim());
  const featuredQuote = testimonials.find((t) => t.featured) || testimonials[0] || null;
  const otherQuotes = testimonials.filter((t) => t !== featuredQuote);
  const relatedServices = (item.service_slugs || []).map((s) => services.find((x) => x.slug === s)).filter(Boolean);

  // Related stories: shared services > shared industry > featured, excluding current
  const related = [...all]
    .filter((c) => c.slug !== item.slug)
    .map((c) => {
      const sharedSvc = (c.service_slugs || []).filter((s) => (item.service_slugs || []).includes(s)).length;
      const sameInd = c.industry && c.industry === item.industry ? 1 : 0;
      return { c, score: sharedSvc * 10 + sameInd * 3 + (c.featured ? 1 : 0) };
    })
    .sort((a, b) => b.score - a.score)
    .slice(0, 3)
    .map((x) => x.c);

  const cta = {
    heading: item.cta_heading || "Could your business be next?",
    text: item.cta_text || "Let's talk about where you want to take your business — no pressure, no obligation.",
    button: item.cta_button || "Let's Talk",
    link: item.cta_link || "/contact",
  };

  const attribution = featuredQuote ? [featuredQuote.name, featuredQuote.job_title, featuredQuote.company].filter(Boolean).join(", ") : "";

  const videoSchema = hasVideo && ytId ? {
    "@context": "https://schema.org", "@type": "VideoObject",
    name: `${item.title} — client video testimonial`,
    description: item.summary || item.subheading || item.title,
    thumbnailUrl: poster || storyImage,
    uploadDate: item.created_at || undefined,
    embedUrl: `https://www.youtube.com/embed/${ytId}`,
    contentUrl: `https://www.youtube.com/watch?v=${ytId}`,
  } : null;

  const QuoteBlock = ({ t, big }) => (
    <blockquote className={big ? "" : "border-l-4 border-orange pl-5 py-1"}>
      <p className={big ? "font-display font-bold text-2xl md:text-3xl leading-snug text-navy" : "text-slate-600 italic leading-relaxed"}>“{t.quote}”</p>
      {[t.name, t.job_title, t.company].filter(Boolean).length > 0 && (
        <footer className={`mt-3 text-sm font-semibold ${big ? "text-orange" : "text-slate-500"}`}>— {[t.name, t.job_title, t.company].filter(Boolean).join(", ")}</footer>
      )}
    </blockquote>
  );

  return (
    <div data-testid="success-story-page">
      <Seo
        title={item.seo_title || `${item.title} — Client Success Story`}
        description={item.meta_description || item.summary || item.subheading || item.title}
        image={item.og_image ? mediaUrl(item.og_image) : storyImage}
        ogTitle={item.og_title || undefined}
        ogDescription={item.og_description || undefined}
        canonical={item.canonical_url || undefined}
        noindex={!!item.noindex}
        type="article"
        jsonLd={[
          breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Success Stories", path: "/testimonials" }, { name: item.title, path: `/success-stories/${item.slug}` }]),
          caseStudySchema(item, { image: storyImage, url: storyUrl }),
          ...(videoSchema ? [videoSchema] : []),
        ]}
      />

      {/* Hero */}
      <section className="numbers-pattern text-white">
        <div className="relative max-w-4xl mx-auto px-6 py-16 md:py-20">
          <Link to="/testimonials" className="flex w-max items-center gap-2 text-slate-300 hover:text-white text-sm font-semibold mb-6"><ArrowLeft size={15} /> Client success stories</Link>
          {item.logo && <img src={mediaUrl(item.logo, 300)} alt={`${item.client_name} logo`} className="h-10 md:h-12 w-auto object-contain mb-5 bg-white/95 rounded-lg px-3 py-1.5" />}
          <span className="block text-orange font-bold text-sm tracking-widest uppercase">Success Story</span>
          <h1 className="font-display font-extrabold text-3xl md:text-5xl leading-tight mt-3">{item.title}</h1>
          {item.subheading && <p className="text-slate-200 text-lg md:text-xl mt-4 leading-relaxed max-w-3xl">{item.subheading}</p>}
          <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-slate-300 text-sm">
            {item.client_name && <span className="inline-flex items-center gap-1.5"><Building2 size={15} className="text-orange" /> {item.client_name}</span>}
            {item.industry && <span className="inline-flex items-center gap-1.5">{item.industry}</span>}
            {item.location && <span className="inline-flex items-center gap-1.5"><MapPin size={14} className="text-orange" /> {item.location}</span>}
          </div>
          {relatedServices.length > 0 && (
            <div className="mt-5 flex flex-wrap gap-2">
              {relatedServices.map((s) => (
                <Link key={s.slug} to={`/services/${s.slug}`} className="text-xs font-semibold bg-white/10 hover:bg-white/20 border border-white/15 rounded-full px-3 py-1 transition-colors">{s.title}</Link>
              ))}
            </div>
          )}
          <ShareRow
            className="mt-6 [&_span]:text-slate-400 [&_button]:text-slate-300 [&_button:hover]:text-white [&_button:hover]:bg-white/10"
            path={`/success-stories/${item.slug}`}
            title={item.title}
            label="Share this story"
          />
        </div>
      </section>

      <section className="py-14 md:py-20 bg-white">
        <div className="max-w-3xl mx-auto px-6">
          {item.image && (
            <Reveal>
              <div className="rounded-2xl overflow-hidden bg-slate-50 mb-12 border border-navy/5">
                <img loading="eager" decoding="async" src={mediaUrl(item.image, 1200)} alt={item.image_alt || item.title} className="w-full h-auto" />
              </div>
            </Reveal>
          )}

          {/* Journey */}
          <div className="space-y-12">
            {activeSections.map((s, idx) => (
              <React.Fragment key={s.key}>
                <Reveal>
                  <div data-testid={`success-section-${s.key}`}>
                    {headingFor(s) && s.key !== "closing" && (
                      <h2 className="font-display font-extrabold text-2xl md:text-3xl text-navy mb-4">{headingFor(s)}</h2>
                    )}
                    {s.key === "closing" && (item.section_headings?.closing) && (
                      <h2 className="font-display font-extrabold text-2xl md:text-3xl text-navy mb-4">{item.section_headings.closing}</h2>
                    )}
                    <div className="story-body"><RichContent text={bodies[s.key]} /></div>
                  </div>
                </Reveal>
                {/* Featured quote inserted as a visual break after The Solution/Difference */}
                {featuredQuote && (s.key === "difference" || (s.key === "solution" && !activeSections.some((x) => x.key === "difference"))) && (
                  <Reveal>
                    <div className="bg-slate-50 rounded-3xl p-8 md:p-10 border border-navy/5" data-testid="success-featured-quote">
                      <Quote size={30} className="text-orange mb-4" />
                      <QuoteBlock t={featuredQuote} big />
                    </div>
                  </Reveal>
                )}
              </React.Fragment>
            ))}

            {legacyParas.length > 0 && (
              <div className="story-body space-y-5">
                {item.summary && <p className="text-slate-700 leading-relaxed text-lg font-medium">{item.summary}</p>}
                <RichContent text={legacyParas.join("\n")} />
              </div>
            )}
          </div>

          {/* Key results */}
          {results.length > 0 && (
            <Reveal>
              <div className="mt-14" data-testid="success-results">
                <h2 className="font-display font-extrabold text-2xl md:text-3xl text-navy mb-6">The results</h2>
                <div className={`grid gap-4 ${results.length >= 3 ? "sm:grid-cols-2" : "sm:grid-cols-2"}`}>
                  {results.map((r, i) => (
                    <div key={i} data-testid={`success-result-${i}`} className="bg-navy text-white rounded-2xl p-6">
                      {r.stat && <p className="font-display font-extrabold text-4xl text-orange leading-none mb-2">{r.stat}</p>}
                      <p className="font-display font-bold text-lg">{r.heading}</p>
                      {r.explanation && <p className="text-slate-300 text-sm mt-1.5 leading-relaxed">{r.explanation}</p>}
                    </div>
                  ))}
                </div>
              </div>
            </Reveal>
          )}

          {/* Featured quote fallback (if no section to attach to) */}
          {featuredQuote && !activeSections.some((s) => s.key === "difference" || s.key === "solution") && (
            <Reveal>
              <div className="bg-slate-50 rounded-3xl p-8 md:p-10 border border-navy/5 mt-14" data-testid="success-featured-quote">
                <Quote size={30} className="text-orange mb-4" />
                <QuoteBlock t={featuredQuote} big />
              </div>
            </Reveal>
          )}

          {/* Video */}
          {hasVideo && (
            <Reveal>
              <div className="mt-14" data-testid="success-story-video">
                <h2 className="font-display font-extrabold text-2xl md:text-3xl text-navy mb-5">Hear it from the client</h2>
                <div className="relative rounded-2xl overflow-hidden shadow-xl bg-navy aspect-video">
                  {uploaded ? (
                    playing ? (
                      <video data-testid="success-story-video-uploaded" className="w-full h-full bg-black" src={mediaUrl(uploaded)} poster={poster || undefined} autoPlay controls playsInline preload="auto" />
                    ) : (
                      <button type="button" data-testid="success-story-video-play" onClick={() => setPlaying(true)} aria-label="Play video" className="group w-full h-full relative">
                        {poster ? <img loading="lazy" decoding="async" src={poster} alt={item.title} className="w-full h-full object-cover" onError={(e) => { e.target.style.display = "none"; }} /> : <div className="w-full h-full" />}
                        <span className="absolute inset-0 bg-navy/40 group-hover:bg-navy/30 transition-colors flex items-center justify-center"><span className="w-16 h-16 rounded-full bg-orange flex items-center justify-center group-hover:scale-110 transition-transform shadow-xl"><Play size={26} className="text-white ml-1" fill="white" /></span></span>
                      </button>
                    )
                  ) : (
                    playing ? (
                      <iframe data-testid="success-story-video-iframe" className="w-full h-full" src={playUrl} title={item.title} frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
                    ) : (
                      <button type="button" data-testid="success-story-video-play" onClick={() => setPlaying(true)} aria-label="Play video" className="group w-full h-full relative">
                        {poster ? <img loading="lazy" decoding="async" src={poster} alt={item.title} className="w-full h-full object-cover" onError={(e) => { if (ytId && !e.target.dataset.fb) { e.target.dataset.fb = "1"; e.target.src = `https://img.youtube.com/vi/${ytId}/hqdefault.jpg`; } }} /> : <div className="w-full h-full" />}
                        <span className="absolute inset-0 bg-navy/40 group-hover:bg-navy/30 transition-colors flex items-center justify-center"><span className="w-16 h-16 rounded-full bg-orange flex items-center justify-center group-hover:scale-110 transition-transform shadow-xl"><Play size={26} className="text-white ml-1" fill="white" /></span></span>
                      </button>
                    )
                  )}
                </div>
              </div>
            </Reveal>
          )}

          {/* Additional testimonials */}
          {otherQuotes.length > 0 && (
            <div className="mt-14 space-y-6" data-testid="success-more-quotes">
              {otherQuotes.map((t, i) => <QuoteBlock key={i} t={t} />)}
            </div>
          )}
        </div>
      </section>

      {/* Services that helped */}
      {relatedServices.length > 0 && (
        <section className="py-14 bg-slate-50" data-testid="success-story-services">
          <div className="max-w-4xl mx-auto px-6">
            <div className="flex items-center gap-2 mb-6">
              <Wrench size={18} className="text-orange" />
              <h2 className="font-display font-bold text-xl md:text-2xl text-navy">Services that helped this client</h2>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              {relatedServices.map((s) => (
                <Link key={s.slug} to={`/services/${s.slug}`} data-testid={`success-story-service-${s.slug}`}
                  className="group flex items-center justify-between gap-3 bg-white rounded-2xl border border-navy/5 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all p-5">
                  <span className="font-semibold text-navy group-hover:text-orange transition-colors">{s.title}</span>
                  <ArrowRight size={18} className="text-orange shrink-0 group-hover:translate-x-1 transition-transform" />
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-16 bg-navy text-white">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="font-display font-extrabold text-3xl md:text-4xl mb-4">{cta.heading}</h2>
          <p className="text-slate-300 mb-8">{cta.text}</p>
          <Link to={cta.link} data-testid="success-story-cta" className="inline-flex items-center gap-2 bg-orange hover:bg-orange-dark text-white font-bold px-8 py-4 rounded-full transition-all hover:-translate-y-0.5">{cta.button} <ArrowRight size={18} /></Link>
        </div>
      </section>

      {/* Related stories */}
      {related.length > 0 && (
        <section className="py-16 bg-white" data-testid="success-related">
          <div className="max-w-6xl mx-auto px-6">
            <h2 className="font-display font-extrabold text-2xl md:text-3xl text-navy mb-8">More client success stories</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {related.map((c) => (
                <Link key={c.id} to={`/success-stories/${c.slug}`} data-testid={`success-related-${c.slug}`} className="group bg-slate-50 rounded-2xl overflow-hidden border border-navy/5 hover:shadow-lg hover:-translate-y-1 transition-all">
                  <div className="aspect-[16/10] overflow-hidden bg-white">{c.image && <img loading="lazy" src={mediaUrl(c.image, 500)} alt={c.image_alt || c.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />}</div>
                  <div className="p-5">
                    {c.industry && <span className="text-xs font-bold uppercase tracking-wide text-orange">{c.industry}</span>}
                    <h3 className="font-display font-bold text-navy leading-snug mt-1 group-hover:text-orange transition-colors line-clamp-2">{c.title}</h3>
                    {c.summary && <p className="text-sm text-slate-500 mt-2 line-clamp-2">{c.summary}</p>}
                    <span className="text-sm font-bold text-orange inline-flex items-center gap-1 mt-3">Read their story <ArrowRight size={14} /></span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
